import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Slide,
  Switch,
  Collapse,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
}

const STORAGE_KEY = 'cookie_consent';

const CookieConsent: React.FC = () => {
  const { t } = useTranslation('modals');
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      // Show banner after a short delay
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const prefs = { essential: true, analytics: true, marketing: true };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    setVisible(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    setVisible(false);
  };

  const handleRejectOptional = () => {
    const prefs = { essential: true, analytics: false, marketing: false };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <Slide direction="up" in={visible}>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          p: 3,
          borderRadius: '16px 16px 0 0',
          maxWidth: 700,
          mx: 'auto',
        }}
      >
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          {t('cookie.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('cookie.description')}
        </Typography>

        <Collapse in={showDetails}>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
              <Box>
                <Typography variant="body2" fontWeight={600}>{t('cookie.essential')}</Typography>
                <Typography variant="caption" color="text.secondary">{t('cookie.essentialDesc')}</Typography>
              </Box>
              <Switch checked disabled />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
              <Box>
                <Typography variant="body2" fontWeight={600}>{t('cookie.analytics')}</Typography>
                <Typography variant="caption" color="text.secondary">{t('cookie.analyticsDesc')}</Typography>
              </Box>
              <Switch
                checked={preferences.analytics}
                onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
              <Box>
                <Typography variant="body2" fontWeight={600}>{t('cookie.marketing')}</Typography>
                <Typography variant="caption" color="text.secondary">{t('cookie.marketingDesc')}</Typography>
              </Box>
              <Switch
                checked={preferences.marketing}
                onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
              />
            </Box>
          </Box>
        </Collapse>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="contained" size="small" onClick={handleAcceptAll}>
            {t('cookie.acceptAll')}
          </Button>
          <Button variant="outlined" size="small" onClick={handleRejectOptional}>
            {t('cookie.essentialOnly')}
          </Button>
          {showDetails ? (
            <Button variant="outlined" size="small" onClick={handleSavePreferences}>
              {t('cookie.savePreferences')}
            </Button>
          ) : (
            <Button variant="text" size="small" onClick={() => setShowDetails(true)}>
              {t('cookie.customize')}
            </Button>
          )}
        </Box>
      </Paper>
    </Slide>
  );
};

export default CookieConsent;
